/**
 * Linha do tempo sem a NOAA (lógica pura, testada).
 *
 * A linha do tempo nasce da listagem da camada de base. Em 20/10/2026 o STAR
 * parou de publicar o GeoColor às 15:00 UTC e, no dia seguinte, ainda não tinha
 * voltado — embora as bandas e os outros compostos do GOES-19 seguissem em dia.
 * Presa à base, a barra de tempo parava junto: focos, fumaça, estações, rios,
 * navios e as outras imagens do satélite ficavam em 12:00 do dia anterior.
 *
 * Com a base atrasada (`ATRASO_NOAA_MS`), a linha do tempo continua numa grade
 * de 10 min até o horário em que a NOAA normalmente já teria publicado
 * (`ATRASO_NORMAL_MS` antes de agora), ou até a imagem mais recente das outras
 * camadas do satélite, se for mais nova. Nesses quadros a base não tem imagem
 * (fica escondida, com aviso) e as demais camadas seguem o horário do quadro.
 * A grade é a mesma do GOES (múltiplos de 10 min, UTC): quando a NOAA volta,
 * as imagens reais ocupam os mesmos horários.
 */
import { MINUTE_MS } from '../lib/time';

/**
 * A NOAA está atrasada quando a imagem mais recente publicada passa disto. O
 * normal é 15–35 min: o STAR publica ~20 min depois do horário do quadro, e
 * os quadros são de 10 em 10. Em 22/09/2026 o GOES-19 parou às 09:50 UTC (uma
 * manutenção no processamento em solo da NOAA) e nada avisava: o atraso
 * parecia defeito do AmaView.
 */
export const ATRASO_NOAA_MS = 45 * MINUTE_MS;

/** Quanto depois do horário do quadro a NOAA costuma publicá-lo. */
export const ATRASO_NORMAL_MS = 20 * MINUTE_MS;

/** Passo da grade dos quadros do GOES-19 (setor NSA). */
export const GRADE_GOES_MS = 10 * MINUTE_MS;

/** Trecho sem a base: do último quadro dela antes da falta até o último quadro da grade. */
export interface TrechoSemBase {
  desde: number;
  ate: number;
}

export interface LinhaDoTempo {
  /** Instantes oferecidos (crescentes): os da base e os da grade onde ela falta. */
  times: readonly number[];
  /** Último quadro publicado da base quando a linha do tempo segue sem ela até agora; null no normal. */
  semBaseDesde: number | null;
  /** Trechos preenchidos pela grade (o do fim, se houver, é o de `semBaseDesde`). */
  trechos: TrechoSemBase[];
}

/** Grade de 10 min depois de `a` até `b` (inclusive). */
function grade(a: number, b: number): number[] {
  const out: number[] = [];
  for (let t = Math.floor(a / GRADE_GOES_MS) * GRADE_GOES_MS + GRADE_GOES_MS; t <= b; t += GRADE_GOES_MS) out.push(t);
  return out;
}

/**
 * Instantes da linha do tempo a partir dos da base (crescentes), do horário da
 * imagem mais recente das outras camadas do satélite e de agora.
 *
 * Também preenche as faltas da base no meio da listagem que passam de
 * `ATRASO_NOAA_MS`: se a NOAA voltar publicando só as imagens novas, a parada
 * vira um buraco de horas, e sem a grade quem estava nele saltaria para antes
 * dele (e "últimas 3 h" ficaria com um ou dois quadros).
 */
export function linhaDoTempo(base: readonly number[], outrasUltimas: readonly number[], agora: number): LinhaDoTempo {
  const ultima = base.at(-1);
  if (ultima === undefined) return { times: base, semBaseDesde: null, trechos: [] };
  const trechos: TrechoSemBase[] = [];
  const times: number[] = [base[0]];
  for (let i = 1; i < base.length; i++) {
    if (base[i] - base[i - 1] > ATRASO_NOAA_MS) {
      const extra = grade(base[i - 1], base[i] - 1);
      times.push(...extra);
      if (extra.length) trechos.push({ desde: base[i - 1], ate: extra[extra.length - 1] });
    }
    times.push(base[i]);
  }
  let semBaseDesde: number | null = null;
  if (agora - ultima > ATRASO_NOAA_MS) {
    const extra = grade(ultima, Math.max(agora - ATRASO_NORMAL_MS, ...outrasUltimas));
    semBaseDesde = ultima;
    times.push(...extra);
    if (extra.length) trechos.push({ desde: ultima, ate: extra[extra.length - 1] });
  }
  return { times: trechos.length ? times : base, semBaseDesde, trechos };
}

/** O trecho sem a base em que o quadro `t` está (ou null). */
export function trechoDoQuadro(trechos: readonly TrechoSemBase[] | null | undefined, t: number | null | undefined): TrechoSemBase | null {
  if (!trechos || t === null || t === undefined) return null;
  return trechos.find((tr) => t > tr.desde && t <= tr.ate) ?? null;
}
